import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import AnonymousLogin from "../../../auth/anonymousLogin";
import VerifyEmail from "../../../auth/anonymousLogin/VerifyEmail";
import {
  triggerSelfEnrolmentVerificationCode,
  getSelfEnrolmentUserData,
  getSelfEnrolmentCompanyData,
  setSelfEnrolmentDetails,
} from "../../../../actions";
import { getErrorMessage, getSelfEnrolmentData } from "../../../../selectors";
import { positions, useAlert } from "react-alert";
import EnrolmentPageMapper from "./EnrolmentPageMapper";

const Login: React.FC = () => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const [isEmailValidated, setIsEmailValidated] = useState(false);
  const [startEnrolment, setStartEnrolment] = useState(false);

  const [
    verificationCodeSentSuccessfully,
    verificationCodeSentFailed,
    email,
    verificationCode,
    sendingVerificationCode,
    getSelfEnrolmentUserDataSuccess,
    selectedCompanyGuid,
    getSelfEnrolmentUserDataFailed,
    getSelfEnrolmentCompanyDataFailed,
    getSelfEnrolmentCompanyDataSuccess,
    errorMessage,
  ] = useSelector((store: any) => [
    getSelfEnrolmentData(store)?.verificationCodeSentSuccessfully,
    getSelfEnrolmentData(store)?.verificationCodeSentFailed,
    getSelfEnrolmentData(store)?.email,
    getSelfEnrolmentData(store)?.verificationCode,
    getSelfEnrolmentData(store)?.sendingVerificationCode,
    getSelfEnrolmentData(store)?.getSelfEnrolmentUserDataSuccess,
    getSelfEnrolmentData(store)?.selectedCompanyGuid,
    getSelfEnrolmentData(store)?.getSelfEnrolmentUserDataFailed,
    getSelfEnrolmentData(store)?.getSelfEnrolmentCompanyDataFailed,
    getSelfEnrolmentData(store)?.getSelfEnrolmentCompanyDataSuccess,
    getErrorMessage(store),
  ]);

  useEffect(() => {
    if (verificationCodeSentSuccessfully === true) {
      setIsEmailValidated(true);
    }
  }, [verificationCodeSentSuccessfully]);

  useEffect(() => {
    if (verificationCodeSentFailed === true) {
      setIsEmailValidated(false);
    }
  }, [verificationCodeSentFailed]);

  useEffect(() => {
    if (getSelfEnrolmentUserDataSuccess === true && selectedCompanyGuid) {
      dispatch(
        getSelfEnrolmentCompanyData({
          guid: selectedCompanyGuid,
          verificationCode,
        })
      );
    } else if (getSelfEnrolmentUserDataSuccess === true) {
      setStartEnrolment(true);
    }
  }, [getSelfEnrolmentUserDataSuccess]);

  useEffect(() => {
    if (getSelfEnrolmentCompanyDataSuccess === true && selectedCompanyGuid) {
      setStartEnrolment(true);
    }
  }, [getSelfEnrolmentCompanyDataSuccess]);

  useEffect(() => {
    if (
      getSelfEnrolmentUserDataFailed === true ||
      getSelfEnrolmentCompanyDataFailed === true
    ) {
      alert.show(
        {
          rawMessage:
            errorMessage.toLowerCase().includes("invalid") &&
            errorMessage.toLowerCase().includes("code")
              ? "Expired/Incorrect verification code. Please click Resend or check email for the correct code"
              : "Failed to get data. Please try again or contact support at support@ayacare.com.",
        },
        {
          position: positions.TOP_CENTER,
          type: "error",
          timeout: 5000,
        }
      );
    }
  }, [getSelfEnrolmentUserDataFailed, getSelfEnrolmentCompanyDataFailed]);

  const onProceedWithEmail = (email: string) => {
    dispatch(
      setSelfEnrolmentDetails({
        verificationCodeSentFailed: false,
      })
    );
    dispatch(triggerSelfEnrolmentVerificationCode(email));
  };

  const resendVerificationCodeHandler = () => {
    dispatch(triggerSelfEnrolmentVerificationCode(email));
  };

  const onProceedToEnrolment = (email: string, verificationCode: string) => {
    dispatch(
      setSelfEnrolmentDetails({
        getSelfEnrolmentUserDataFailed: false,
        getSelfEnrolmentCompanyDataFailed: false,
      })
    );
    dispatch(getSelfEnrolmentUserData({ email, verificationCode }));
  };

  const selfEnrolmentText = (
    <div
      style={{
        fontFamily: "Montserrat",
        lineHeight: "1.8rem",
        justifyContent: "flex-start",
      }}
    >
      <p style={{ fontWeight: "bold", fontSize: "20px", marginTop: "15px" }}>
        We're absolutely thrilled that you're considering enroling with us!
      </p>
      <p>
        To get started, we just need a valid email address to begin the process.
        Don't worry, your email will only be used to keep you informed about the
        enrolment process.
      </p>
      <p>
        By sharing your email, you're not just starting a process; you're
        igniting a partnership that we're genuinely excited about!
      </p>
    </div>
  );

  return (
    <div>
      {!startEnrolment && (
        <>
          {!isEmailValidated && (
            <AnonymousLogin
              proceedActionHandler={onProceedWithEmail}
              heading={"Let's Partner! Your Enrolment Starts Here."}
              contentChildren={selfEnrolmentText}
            ></AnonymousLogin>
          )}
          {isEmailValidated && (
            <>
              <VerifyEmail
                email={email}
                resendTriggerHandler={resendVerificationCodeHandler}
                loading={sendingVerificationCode}
                timerDuration={300}
                proceedActionHandler={onProceedToEnrolment}
              />
            </>
          )}
        </>
      )}
      {startEnrolment && <EnrolmentPageMapper />}
    </div>
  );
};

export default Login;
